import type { TFunction } from 'i18next'

/**
 * Station-unique FAQ content (TASK-699, Figma 8597:41876). Q&As are
 * assembled deterministically from data the page already has, so
 * every station gets its own set and the copy can be prerendered.
 * A question whose fact is missing is omitted — never answered with
 * a guess (TASK-589 rule).
 *
 * All copy resolves through the `station-page` i18n namespace as FULL
 * sentences per key (no concatenated fragments — inflected languages
 * break on fragments); list joins use `Intl.ListFormat` so "A, B and C"
 * localizes while the proper nouns stay English.
 */

export interface StationFaqInput {
  stationName: string
  zone: string | undefined
  lineNames: readonly string[]
  stepFree: boolean
  hasToilets: boolean
  /** First exit + landmark pair from the station-content exits. */
  exitLandmark: { exit: string; landmark: string } | undefined
  /** openTodayRange output ("05:02 – 00:41") or null. */
  openRange: string | null
}

export interface StationFaqEntry {
  question: string
  answer: string
}

/**
 * Locale-aware "A, B and C" (falls back to comma-joins where ICU lacks
 * data). Bare `en` resolves to the US Oxford-comma style — the site's
 * voice is British (TASK-588), so it maps to `en-GB`.
 */
export function listFormatLocale(locale: string): string {
  return locale === 'en' ? 'en-GB' : locale
}

export function formatList(items: readonly string[], locale: string): string {
  if (items.length === 0) return ''
  try {
    return new Intl.ListFormat(listFormatLocale(locale), { type: 'conjunction' }).format([...items])
  } catch {
    return items.join(', ')
  }
}

/** First exit with a nearby landmark → the "Which exit for X?" pair. */
export function exitLandmarkFromExits(
  exits: readonly { name: string; nearby?: string }[],
): { exit: string; landmark: string } | undefined {
  for (const exit of exits) {
    if (exit.nearby === undefined) continue
    const landmark = exit.nearby.split('·')[0].trim()
    if (landmark !== '') return { exit: exit.name, landmark }
  }
  return undefined
}

export function buildStationFaq(
  input: StationFaqInput,
  t: TFunction<'station-page'>,
  locale: string,
): StationFaqEntry[] {
  const { stationName, zone, lineNames, stepFree, hasToilets, exitLandmark, openRange } = input
  const entries: StationFaqEntry[] = []

  if (lineNames.length > 0) {
    entries.push({
      question: t('faq_q_lines', { station: stationName }),
      answer: t('faq_a_lines', {
        station: stationName,
        lines: formatList(lineNames, locale),
        count: lineNames.length,
      }),
    })
  }

  if (zone !== undefined) {
    entries.push({
      question: t('faq_q_zone', { station: stationName }),
      answer: t('faq_a_zone', { station: stationName, zone }),
    })
  }

  entries.push({
    question: t('faq_q_step_free', { station: stationName }),
    answer: stepFree
      ? t('faq_a_step_free_yes', { station: stationName })
      : t('faq_a_step_free_no', { station: stationName }),
  })

  entries.push({
    question: t('faq_q_toilets', { station: stationName }),
    answer: hasToilets
      ? t('faq_a_toilets_yes', { station: stationName })
      : t('faq_a_toilets_no', { station: stationName }),
  })

  if (exitLandmark !== undefined) {
    entries.push({
      question: t('faq_q_exit', { landmark: exitLandmark.landmark }),
      answer: t('faq_a_exit', { exit: exitLandmark.exit, landmark: exitLandmark.landmark }),
    })
  }

  if (openRange !== null) {
    const [first, last] = openRange.split(' – ')
    entries.push({
      question: t('faq_q_hours', { station: stationName }),
      answer: t('faq_a_hours', { station: stationName, first, last }),
    })
  }

  return entries
}
